import axios from 'axios';
import JSONbig from 'json-bigint';

import {
  DEFAULT_ERROR_MESSAGE,
  ERR_NETWORK_CODE,
  ERROR,
  SERVER_UNAVAILABLE_MESSAGE,
  WARNING,
} from '@app/constants';
import { extractHeadersForLogs } from '@app/services/__global__/extractHeadersForLogs';
import { serverStatus } from '@app/services/__global__/serverStatus';
import { setAxiosConfig } from '@app/services/__global__/setAxiosConfig';
import { authenticateByCookie } from '@app/services/authentication/authenticateByCookie';
import type { NAuth } from '@app/types';
import { hasToken } from '@app/utils/hasToken';

import type { SerializedError } from '@reduxjs/toolkit';
import type { BaseQueryApi } from '@reduxjs/toolkit/query';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

type JsonDataParsed = unknown;

const isString = (value: unknown): value is string => typeof value === 'string';

const looksLikeJson = (text: string): boolean => {
  const trimmed = text.trim();
  if (!trimmed) return false;

  const [first] = trimmed;
  if (first === '{' || first === '[' || first === '"') return true;

  if (first === '-' || (first >= '0' && first <= '9')) return true;

  return (
    trimmed === 'true' ||
    trimmed === 'false' ||
    trimmed === 'null' ||
    trimmed.startsWith('true') ||
    trimmed.startsWith('false') ||
    trimmed.startsWith('null')
  );
};

export const safeParseJsonData = (jsonData: unknown): JsonDataParsed => {
  if (!isString(jsonData)) return jsonData;
  if (!looksLikeJson(jsonData)) return jsonData;

  try {
    return JSONbig.parse(jsonData);
  } catch {
    return jsonData;
  }
};

export type IBaseQueryParams = AxiosRequestConfig & {
  logs?: {
    isLogged: boolean;
    userId: NAuth.User['userId'];
    database: NAuth.User['database'];
  };
  isBookHost?: boolean;
  isRawResponse?: boolean;
};

export type ErrorT =
  | {
      status: string;
      data: string;
    }
  | SerializedError
  | undefined;

let isLogoutTriggered = false;
const triggerLogoutOnce = async () => {
  if (isLogoutTriggered) return;
  isLogoutTriggered = true;

  try {
    const { logOutUser } = await import('@app/utils/logOutUser');
    void logOutUser();
  } finally {
    setTimeout(() => {
      isLogoutTriggered = false;
    }, 0);
  }
};

const SERVER_UNAVAILABLE_STATUSES = [502, 503, 504];

// Тело ответа инфраструктуры (страница ошибки nginx) — не текст для пользователя.
const isHtmlBody = (error: AxiosError, body: string): boolean => {
  const contentType = error.response?.headers?.['content-type'];

  if (typeof contentType === 'string' && contentType.includes('text/html')) {
    return true;
  }

  return body.trim().startsWith('<');
};

const extractAxiosErrorMessage = (error: unknown): string => {
  if (!axios.isAxiosError(error)) return '';

  const status = error.response?.status;

  // Пока бэкенд перезапускается при выкате, приходит либо 502/503/504 со
  // страницей nginx в теле, либо сетевая ошибка с текстом «Network Error».
  if (
    (status !== undefined && SERVER_UNAVAILABLE_STATUSES.includes(status)) ||
    error.code === ERR_NETWORK_CODE
  ) {
    return SERVER_UNAVAILABLE_MESSAGE;
  }

  const data = error.response?.data;

  if (typeof data === 'string' && data.trim()) {
    return isHtmlBody(error, data) ? DEFAULT_ERROR_MESSAGE : data;
  }

  // если бекенд вернул объект — попробуем вытащить знакомые поля
  if (data && typeof data === 'object') {
    const maybeErrorMessage = (data as { ErrorMessage?: unknown }).ErrorMessage;
    if (typeof maybeErrorMessage === 'string' && maybeErrorMessage.trim()) {
      return maybeErrorMessage;
    }

    const maybeMessage = (data as { message?: unknown }).message;
    if (typeof maybeMessage === 'string' && maybeMessage.trim()) {
      return maybeMessage;
    }
  }

  return error.message;
};

export const customBaseQuery = async (
  { ...args }: IBaseQueryParams,
  api?: Pick<BaseQueryApi, 'signal'>
) => {
  const {
    logs,
    headers,
    responseType,
    isBookHost,
    isRawResponse,
    signal,
    ...axiosConfig
  } = args;
  const requestSignal = signal ?? api?.signal;

  let response: AxiosResponse;
  let isSuccess = false;

  try {
    if (!hasToken()) {
      await authenticateByCookie();
    }

    // ВАЖНО: в setAxiosConfig должен быть return Promise.reject(error) в interceptor’е,
    // иначе axios-ошибки будут "глотаться" и сюда не доходить как AxiosError.
    setAxiosConfig(isBookHost);

    if (logs?.isLogged) {
      const { userId, database } = logs;
      const logHeaders =
        (await extractHeadersForLogs({ userId, database })) ?? {};

      response = await axios({
        responseType,
        headers: { ...headers, ...logHeaders },
        ...axiosConfig,
        signal: requestSignal,
      });
    } else {
      response = await axios({
        responseType,
        headers,
        ...axiosConfig,
        signal: requestSignal,
      });
    }

    if (!responseType || responseType === 'json') {
      if (isBookHost || isRawResponse) {
        return { data: response.data };
      }

      if (typeof response.data === 'string') {
        serverStatus.recordFailure();

        if (serverStatus.isDown) {
          return { error: { status: 'MAINTENANCE', data: '' } };
        }

        throw new Error('', { cause: ERROR });
      }

      const { JsonData, ErrorMessage } = response.data as {
        JsonData?: unknown;
        ErrorMessage?: string;
        Success?: boolean;
      };

      const parsedJsonData = safeParseJsonData(JsonData);

      const isSuccessByCode = response.status === 200;
      const isSuccessByServerResponse = Boolean(
        (response.data as { Success?: boolean } | undefined)?.Success
      );

      isSuccess = isSuccessByCode && isSuccessByServerResponse;

      if (!isSuccess) {
        const serverErrorText = (
          response.data as { ErrorMessage?: string } | undefined
        )?.ErrorMessage;
        const { statusText } = response;

        const errorDescription = isSuccessByServerResponse
          ? statusText
          : serverErrorText || '';

        throw new Error(errorDescription, { cause: ERROR });
      }

      if (ErrorMessage) {
        throw new Error(ErrorMessage, { cause: WARNING });
      }

      return { data: parsedJsonData };
    }

    return { data: response.data };
  } catch (unknownError: unknown) {
    if (serverStatus.isDown) {
      return { error: { status: 'MAINTENANCE', data: '' } };
    }

    if (axios.isAxiosError(unknownError)) {
      const status = unknownError.response?.status;

      if (status === 401) {
        void triggerLogoutOnce();
      }

      const message =
        extractAxiosErrorMessage(unknownError) || 'Request failed';

      return { error: { status: 'ERROR', data: message } };
    }

    const error =
      unknownError instanceof Error
        ? unknownError
        : new Error(String(unknownError));

    if (error.cause === ERROR) {
      return { error: { status: 'ERROR', data: error.message } };
    }

    if (error.cause === WARNING) {
      return { error: { status: 'WARNING', data: error.message } };
    }

    return { error: { status: 'ERROR', data: error.message } };
  }
};
